import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKayanYazilarUcerOyunu } from './hooks/useKayanYazilarUcerOyunu';
import GameHeader from './components/GameHeader';
import GameInstructions from './components/GameInstructions';
import GameControls from './components/GameControls';
import GameDisplay from './components/GameDisplay';
import GameOverModal from './components/GameOverModal';
import GameTimerUcer from './components/GameTimer';
import { API_URLS } from '../../../config/config';
import axiosInstance from '../../../utils/axiosInstance';

// Stil
import './styles/kayanYazilarUcerStyle.css';

const KayanYazilarUcerOyunu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stage, exercise } = location.state || {};

  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const [showInstructions, setShowInstructions] = useState(true);
  const [showGameOver, setShowGameOver] = useState(false);

  const token = localStorage.getItem('userToken');

  // useKayanYazilarUcerOyunu hook’una stage bilgisi geçiyoruz
  const {
    difficulty,
    paragraphsList,
    selectedParagraphIndex,
    displayedWords,
    isPlaying,
    speed,
    elapsedTime,
    gameTime,
    timeLeft,
    message,
    gameOver,
    setGameOver,
    updateWords,
    handlePlayPause,
    handleReset,
    handleSpeedChange,
    handleParagraphChange,
    handleDifficultyChange,
    onTimeUp,
    difficultyLocked,
  } = useKayanYazilarUcerOyunu(navigate, userData, token, stage);

  // Kullanıcı verisi: önce öğrenci, yoksa öğretmen
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const studentResponse = await axiosInstance.get(`${API_URLS.STUDENT_URL}/me`);
        if (
          studentResponse.status === 200 &&
          studentResponse.data &&
          studentResponse.data.status === 'active'
        ) {
          const studentData = studentResponse.data;
          setUserData(studentData);
          setUserRole('student');
          return;
        } else {
          setUserData(null);
          setUserRole(null);
        }
      } catch (error) {
        console.error('Öğrenci verisi alınırken hata oluştu:', error);
      }

      // Öğrenci yoksa veya aktif değilse öğretmen verisine bak
      try {
        const teacherResponse = await fetch(`${API_URLS.TEACHER_URL}/me`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (teacherResponse.ok) {
          const teacherData = await teacherResponse.json();
          setUserData(teacherData);
          setUserRole('teacher');
          return;
        }
      } catch (error) {
        console.error('Öğretmen verisi alınırken hata oluştu:', error);
      }

      setUserData(null);
      setUserRole(null);
    };

    fetchUserData();
  }, [token]);

  // Oyunu başlat
  const startGame = () => {
    handleReset();
    setShowInstructions(false);
  };

  // gameOver true olduğunda "Oyun Bitti" modalını aç
  useEffect(() => {
    if (gameOver && !showGameOver) {
      setShowGameOver(true);
    }
  }, [gameOver, showGameOver]);

  // Oyun bittiğinde ilerlemeyi güncelle
  useEffect(() => {
    if (gameOver) {
      const updateProgress = async () => {
        try {
          if (!userData || !userRole) return;
          const payload = {
            stageNumber: stage?.number || 1,
            gameName: exercise?.gameName || 'Kayan Yazılar 3',
            // Score: her 3’lü grup 3 kelime olduğu için displayedWords.length * 3
            score: displayedWords.length * 3,
            time: Math.floor(elapsedTime),
            wordCount: displayedWords.length * 3,
            completed: true,
          };

          if (userRole === 'student') {
            // Öğrenci -> axiosInstance ile
            const apiUrl = `${API_URLS.STUDENT_URL}akillikurs/progress/update-game`;
            await axiosInstance.post(apiUrl, payload);
          } else {
            // Öğretmen -> fetch veya axios ile token eklenir
            const apiUrl = `${API_URLS.TEACHER_URL}akillikurs/progress/update-game`;
            const teacherToken = localStorage.getItem('userToken');
            await axios.post(apiUrl, payload, {
              headers: {
                Authorization: `Bearer ${teacherToken}`,
                'Content-Type': 'application/json',
              },
            });
          }
        } catch (error) {
          console.error('İlerleme güncellenirken hata oluştu:', error);
        }
      };
      updateProgress();
    }
  }, [gameOver, displayedWords, elapsedTime, userData, userRole, stage, exercise]);

  // Oyunu tekrar başlat
  const handleRestart = () => {
    setShowGameOver(false);
    setGameOver(false);
    handleReset();
  };

  // Önceki sayfaya / stage’e dön
  const handleReturnToPreviousStage = () => {
    if (stage) {
      navigate(
        userRole === 'student' ? '/ogrenci-dashboard' : '/ogretmen-dashboard',
        {
          state: {
            selectedMenu: 'ExerciseList',
            selectedStage: stage,
          },
        }
      );
    } else {
      navigate(-1);
    }
  };

  // GameOverModal kapat
  const handleClose = () => {
    setShowGameOver(false);
    setGameOver(false);
  };

  return (
    <>
      {/* Oyun başlamadan önce bilgilendirme ekranı */}
      {showInstructions ? (
        <div className="kayan-yazilar-ucer-intro-container">
          <div className="kayan-yazilar-ucer-intro">
            <div className="kayan-yazilar-ucer-card">
              <div className="kayan-yazilar-ucer-card-body">
                <GameHeader handleReturnToPreviousStage={handleReturnToPreviousStage} />
                <h1 className="kayan-yazilar-ucer-title">Kayan Yazılar 3 Çalışması</h1>
                {stage ? (
                  <p className="kayan-yazilar-ucer-description">
                    Bu çalışmada aşamanıza göre zorluk seviyesi otomatik belirlenecek. 
                    Üçer kelime halinde ekranda beliren kelimeleri en hızlı şekilde okuyun.
                  </p>
                ) : (
                  <GameInstructions />
                )}
                <button className="kayan-yazilar-ucer-button" onClick={startGame}>
                  BAŞLAT
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Oyun ekranı
        <div className="kayan-yazilar-ucer-game-container">
          <div className="kayan-yazilar-ucer-gameplay">
            <GameHeader handleReturnToPreviousStage={handleReturnToPreviousStage} />

            {/* Oyun kontrolleri */}
            <GameControls
              difficulty={difficulty}
              paragraphsList={paragraphsList}
              selectedParagraphIndex={selectedParagraphIndex}
              speed={speed}
              handleDifficultyChange={handleDifficultyChange}
              handleParagraphChange={handleParagraphChange}
              handleSpeedChange={handleSpeedChange}
              difficultyLocked={difficultyLocked}
            />

            {/* Timer + ProgressBar */}
            <GameTimerUcer
              timeLeft={timeLeft}
              isPlaying={isPlaying}
              gameTime={gameTime}
              elapsedTime={elapsedTime}
            />

            {/* Ana içerik (3’lü kelimeler) */}
            <GameDisplay
              isPlaying={isPlaying}
              speed={speed}
              paragraphsList={paragraphsList}
              selectedParagraphIndex={selectedParagraphIndex}
              displayedWords={displayedWords}
              updateWords={updateWords}
              handlePlayPause={handlePlayPause}
              handleReset={handleReset}
              message={message}
              gameOver={gameOver}
            />
          </div>
        </div>
      )}

      {/* Oyun Bitti Modal */}
      {showGameOver && (
        <GameOverModal
          // 3’lü olduğu için displayedWords.length * 3 kelime
          score={displayedWords.length * 3}
          // elapsedTime saniye cinsinden
          elapsedTime={Math.floor(elapsedTime)}
          onRestart={handleRestart}
          onClose={handleClose}
          userData={userData}
        />
      )}
    </>
  );
};

export default KayanYazilarUcerOyunu;
